import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap';
import { createPO, getVendorList } from '../services/po.services';
import { HttpStatus } from 'pages/Core';
import notistack from 'pages/Core/Notistack';
import { FieldConfig } from 'pages/Componets/ItemMaster/model/FieldConfig';
import { poCreationFields } from './fieldConfig';
import { useForm, useFieldArray } from 'react-hook-form';
import { RenderInput } from 'pages/Componets/ItemMaster/model/FieldConfig';
import { useNavigate } from 'react-router-dom';

interface POprocessModalProp {
    isPoOpen: boolean;
    selectedRowsRef: any[] | [];
    onClose: () => void;
    reloadDataSource: () => void;
}

interface POFormData {
    vendorId: string;
    products: {
        productHeaderDetailItemMasterId: number;
        productName: string;
        quantity: number | string;
        poDate: string | Date;
        standardGoldWeight: number | string;
        standardDiamondWeight: number | string;
        expectedGoldRate: number | string;
        expectedDiamondRate: number | string;
        expectedMakingRate: number | string;
        specialInstructions: string;
    }[];
}

const POprocessModal: React.FC<POprocessModalProp> = ({ isPoOpen, selectedRowsRef, onClose, reloadDataSource }) => {
    const [headerFields, setHeaderFields] = useState<FieldConfig[]>(poCreationFields);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<POFormData>();

    const { fields } = useFieldArray({
        control,
        name: "products"
    });

    const mapOptions = (data: any[]) =>
        data?.map(item => ({
            label: item.text,
            value: item.value
        })) || [];

    const getVendors = async () => {
        try {
            const response = await getVendorList()
            if (response.status === HttpStatus.Success) {
                const data = response.data;

                setHeaderFields(prev =>
                    prev.map(field => {
                        switch (field.name) {
                            case "vendorId":
                                return { ...field, options: mapOptions(data) };
                            default:
                                return field;
                        }
                    })
                );
            }
        } catch (error: any) {
            notistack.error(error)
        }
    }

    useEffect(() => {
        if (isPoOpen) {
            getVendors();

            reset({
                vendorId: '',
                products: selectedRowsRef.map(product => ({
                    productHeaderDetailItemMasterId: product?.id,
                    productName: product?.productName,
                    quantity: '',
                    // poDate: '',
                    standardGoldWeight: '',
                    standardDiamondWeight: '',
                    expectedGoldRate: '',
                    expectedDiamondRate: '',
                    expectedMakingRate: '',
                    specialInstructions: '',
                }))
            });
        }
    }, [isPoOpen, selectedRowsRef, reset]);

    const clearAllFormFields = () => {
        reset({
            vendorId: '',
            products: fields.map(product => ({
                ...product,
                quantity: '',
                // poDate: new Date().toISOString().split('T')[0],
                expectedGoldRate: '',
                expectedDiamondRate: '',
                expectedMakingRate: '',
                specialInstructions: ''
            }))
        });
    };

    const navigate = useNavigate()

    const onSubmit = async (data: POFormData) => {
        try {
            setIsSubmitting(true);
            const payload = data.products.map(product => ({
                vendorId: parseInt(data.vendorId),
                productHeaderDetailItemMasterId: Number(product.productHeaderDetailItemMasterId),
                quntity: Number(product.quantity),
                // poDate: new Date(product.poDate).toISOString(),
                processPOModel: {
                    standardGoldWeight: Number(product.standardGoldWeight),
                    standardDiamondWeight: Number(product.standardDiamondWeight),
                    expectedGoldRate: Number(product.expectedGoldRate),
                    expectedDiamondRate: Number(product.expectedDiamondRate),
                    expectedMakingRate: Number(product.expectedMakingRate),
                    specialInstructions: (product.specialInstructions?.trim())
                }
            }));
            const response = await createPO(payload)
            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response)
                onClose();
                clearAllFormFields()
                reloadDataSource();
                navigate("/po-orders")
            } else {
                notistack.notifyFromApi(response)
            }
        } catch (error: any) {
            notistack.error(error.message || 'Failed to create PO');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Modal
                show={isPoOpen}
                onHide={() => {
                    clearAllFormFields()
                    onClose()
                }}
                backdrop="static"
                size="lg"
                dialogClassName="modal-dialog-centered"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Create Purchase Order</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <form id="poForm" onSubmit={handleSubmit(onSubmit)}>
                        <div className="row mb-4 border-bottom pb-3">
                            <div className="col-md-12">
                                {headerFields
                                    .filter(f => f.name === "vendorId")
                                    .map((f) => (
                                        <RenderInput
                                            key={f.name}
                                            placeholder={`Select ${f.label}`}
                                            field={f}
                                            register={register}
                                            errors={errors}
                                            control={control}
                                        />
                                    ))}
                            </div>
                        </div>

                        <div className="products-section">
                            {fields.map((field, index) => {
                                const product = selectedRowsRef[index];
                                const productFieldErrors: any = {};
                                if (errors.products?.[index]) {
                                    const productErrorObj: any = errors?.products[index];
                                    Object.keys(productErrorObj)?.forEach(key => {
                                        productFieldErrors[`products.${index}.${key}`] = productErrorObj[key];
                                    });
                                }

                                return (
                                    <div key={field.id} className="card mb-4">
                                        <div className="card-header">
                                            <h6 className="mb-0">Product: {product?.productName}</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row g-3">
                                                <input
                                                    type="hidden"
                                                    {...register(`products.${index}.productHeaderDetailItemMasterId`)}
                                                />

                                                <RenderInput
                                                    field={{
                                                        name: `products.${index}.quantity`,
                                                        label: "Quantity",
                                                        type: "number",
                                                        required: true
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Enter quantity"
                                                    rules={{
                                                        min: {
                                                            value: 1,
                                                            message: "Quantity must be greater than 0"
                                                        },
                                                        validate: (value: any) => {
                                                            if (Number(value) <= 0) {
                                                                return "Quantity must be greater than 0";
                                                            }
                                                            return true;
                                                        }
                                                    }}
                                                />

                                                {/* <RenderInput
                                                    field={{
                                                        name: `products.${index}.poDate`,
                                                        label: "PO Date",
                                                        type: "date",
                                                        required: true
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Select date"
                                                /> */}

                                                {/* <RenderInput
                                                    field={{
                                                        name: `products.${index}.standardGoldWeight`,
                                                        label: "Standard Gold Wt",
                                                        type: "decimal",
                                                        required: true
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Enter gold weight"
                                                />

                                                <RenderInput
                                                    field={{
                                                        name: `products.${index}.standardDiamondWeight`,
                                                        label: "Standard Diamond Wt",
                                                        type: "decimal",
                                                        required: true
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Enter diamond weight"
                                                /> */}

                                                <RenderInput
                                                    field={{
                                                        name: `products.${index}.expectedGoldRate`,
                                                        label: "Expected Gold Rate",
                                                        type: "decimal2",
                                                        required: true
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Enter gold rate"
                                                />

                                                {/* <RenderInput
                                                    field={{
                                                        name: `products.${index}.expectedDiamondRate`,
                                                        label: "Expected Diamond Rate",
                                                        type: "decimal2",
                                                        required: true
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Enter diamond rate"
                                                />

                                                <RenderInput
                                                    field={{
                                                        name: `products.${index}.expectedMakingRate`,
                                                        label: "Expected Making Rate",
                                                        type: "decimal2",
                                                        required: true
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Enter making rate"
                                                /> */}

                                                <RenderInput
                                                    field={{
                                                        name: `products.${index}.specialInstructions`,
                                                        label: "Special Instructions",
                                                        type: "text",
                                                        required: false
                                                    }}
                                                    register={register}
                                                    errors={productFieldErrors}
                                                    control={control}
                                                    placeholder="Enter special instructions"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {selectedRowsRef.length === 0 && (
                            <div className="alert alert-warning">
                                No products selected for PO creation.
                            </div>
                        )}
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn add-btn me-2"
                        onClick={() => {
                            clearAllFormFields()
                            onClose()
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="poForm"
                        className="btn submit-btn ms-2"
                        disabled={isSubmitting || selectedRowsRef.length === 0}
                    >
                        {isSubmitting ? 'Creating...' : 'Create PO'}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default POprocessModal;